import { createSlice, PayloadAction, createAction } from "@reduxjs/toolkit";
import { RootState } from "../../redux-store";
import axios, { getData } from "../../axios";
import * as types from "./types";
import * as cargoActions from "../cargo";
import * as tPromise from "io-ts-promise";
import keyBy from "lodash/keyBy";
import sortBy from "lodash/sortBy";
import { cargoDeleted } from "../app/cargo-dialog";
import {
  routeT,
  archivedRoutesResponseT,
  ArchivedRoutesResponse,
  routeInfoStopsT,
  updateRouteVehicleT,
} from "dora-contracts";
import { notifyL } from "../notifications";
import { createErrorReportingAsyncThunk, unwrap } from "../helpers";
import pickBy from "lodash/pickBy";
import { RouteInfoStop, WaypointData } from "../app/route-info/stops/types";
import * as dispatchingActions from "../app/dispatching";
import { SortOrder, RouteSortProperty, RouteSortSelection } from "./types";

const prefix = "routes";

export const routeUpdated = createAction<types.RouteT>(
  `${prefix}/route-updated`
);

export const stopOrderUpdated = createAction(
  `${prefix}/stop-order-updated`,
  (input: { routeId: string; stops: RouteInfoStop[] }) => {
    return { payload: input };
  }
);

export const addDriver = createErrorReportingAsyncThunk(
  "route/addDriver",
  async (input: { routeId: string; driverId: string }, { dispatch }) => {
    const { routeId, driverId } = input;
    try {
      await axios.put(`/api/routes/${routeId}/drivers/${driverId}`);
    } catch (e) {
      dispatch(
        notifyL({
          namespace: "notifications",
          key: "addDriverToRouteFailed",
          type: "error",
        })
      );
      throw e;
    }
  },
  { skipNotification: true }
);

export const removeDriver = createErrorReportingAsyncThunk(
  "route/removeDriver",
  async (input: { routeId: string; driverId: string }, { dispatch }) => {
    const { routeId, driverId } = input;
    try {
      await axios.delete(`/api/routes/${routeId}/drivers/${driverId}`);
    } catch (e) {
      dispatch(
        notifyL({
          namespace: "notifications",
          key: "removeDriverFromRouteFailed",
          type: "error",
        })
      );
      throw e;
    }
  },
  { skipNotification: true }
);

export const getRouteStops = createErrorReportingAsyncThunk(
  `${prefix}/getStops`,
  async (routeId: string) => {
    const data = await axios
      .get(`/api/routes/${routeId}/stops`)
      .then(getData)
      .then(tPromise.decode(routeInfoStopsT));
    return data.map((x) => ({ ...x, stopId: (x as any).stopId || x.id }));
  }
);

export const ARCHIVED_SEARCH_SUPERSEDED = "ARCHIVED_SEARCH_SUPERSEDED";

export const getAllArchivedRoutes = createErrorReportingAsyncThunk<
  ArchivedRoutesResponse,
  types.SearchInputs,
  { rejectValue: typeof ARCHIVED_SEARCH_SUPERSEDED }
>(
  `${prefix}/getArchivedRoutesBySearch`,
  async (searchInputs, { getState, requestId, rejectWithValue }) => {
    const result = await axios.get("/api/routes/archived", {
      params: pickBy(searchInputs, (x) => x),
    });
    const { routes, cargoViews, routeViews } = await tPromise.decode(
      archivedRoutesResponseT,
      result.data
    );
    // The search was cleared while this request was in flight. Its routes
    // must not replace what is shown now.
    if (getState().routes.archivedSearchRequestId !== requestId) {
      return rejectWithValue(ARCHIVED_SEARCH_SUPERSEDED);
    }
    return { routes, cargoViews, routeViews };
  },
  {
    // One archived search at a time. A search can take seconds and return
    // megabytes; repeated clicks used to start one request each, and the
    // slowest response won whichever order they were sent in.
    condition: (_arg, { getState }) =>
      getState().routes.archivedSearchRequestId === null,
  }
);

export const unarchiveRoute = createErrorReportingAsyncThunk(
  `${prefix}/unarchiveRoute`,
  async (route: { id: string }) => {
    await axios.post(`/api/routes/${route.id}/unarchive`, {
      id: route.id,
    });
  }
);

export const addWaypoint = createErrorReportingAsyncThunk(
  `${prefix}/addWaypoint`,
  async (input: { routeId: string; waypoint: WaypointData }) => {
    const { routeId, waypoint } = input;
    await axios.post(`/api/routes/${routeId}/waypoints`, waypoint);
  }
);

export const editWaypoint = createErrorReportingAsyncThunk(
  `${prefix}/editWaypoint`,
  async (input: { routeId: string; waypoint: WaypointData }) => {
    const { routeId, waypoint } = input;
    await axios.put(
      `/api/routes/${routeId}/waypoints/${waypoint.id}`,
      waypoint
    );
  }
);

export const deleteWaypoint = createErrorReportingAsyncThunk(
  `${prefix}/deleteWaypoint`,
  async (input: { routeId: string; waypointId: string }) => {
    const { routeId, waypointId } = input;
    await axios.delete(`/api/routes/${routeId}/waypoints/${waypointId}`);
  }
);

export const updateRouteName = createErrorReportingAsyncThunk(
  `${prefix}/ROUTE_UPDATE_NAME`,
  async (route: { id: string; route_name: string }) => {
    await axios.put(`/api/routes/update-name/`, {
      id: route.id,
      route_name: route.route_name,
    });
  }
);

export const assignToTeam = createErrorReportingAsyncThunk(
  `${prefix}/assign-to-team`,
  async ({ routeId, teamId }: { routeId: string; teamId: string }) => {
    return axios
      .patch(`/api/routes/${routeId}`, { teamId })
      .then(getData)
      .then(tPromise.decode(routeT));
  }
);

export interface UpdateRadiusArgs {
  routeId: string;
  radius: number;
}
export const updateRouteRadius = createErrorReportingAsyncThunk(
  `${prefix}/ROUTE_UPDATE_RADIUS`,
  async (args: UpdateRadiusArgs) => {
    await axios.post(`/api/routes/radius`, {
      id: args.routeId,
      radius: args.radius,
    });
  }
);

export const updateRouteTruck = createErrorReportingAsyncThunk(
  `${prefix}/ROUTE_UPDATE_TRUCK`,
  async (route: { id: string; truckId: string }, { dispatch }) => {
    const res = await axios.post(
      `/api/routes/update-truck`,
      updateRouteVehicleT.encode({
        id: route.id,
        vehicleId: route.truckId,
      })
    );
    if (res.data.warnMultipleActiveRoutes) {
      dispatch(
        notifyL({
          namespace: "notifications",
          key: "warnMultipleActiveRoutes",
          type: "warning",
        })
      );
    }
    dispatch(getRouteStops(route.id));
  }
);

export const removeRouteTruck = createErrorReportingAsyncThunk(
  `${prefix}/ROUTE_REMOVE_TRUCK`,
  async (route: { id: string }, { dispatch }) => {
    await axios.post(`/api/routes/remove-truck`, { id: route.id });
    dispatch(getRouteStops(route.id));
  }
);

export const updateRouteTrailer = createErrorReportingAsyncThunk(
  `${prefix}/ROUTE_UPDATE_TRAILER`,
  async (route: { id: string; trailerId: string }, { dispatch }) => {
    const res = await axios.post(
      `/api/routes/update-trailer`,
      updateRouteVehicleT.encode({
        id: route.id,
        vehicleId: route.trailerId,
      })
    );
    if (res.data.warnMultipleActiveRoutes) {
      dispatch(
        notifyL({
          namespace: "notifications",
          key: "warnMultipleActiveRoutes",
          type: "warning",
        })
      );
    }
    dispatch(getRouteStops(route.id));
  }
);

export const removeRouteTrailer = createErrorReportingAsyncThunk(
  `${prefix}/ROUTE_REMOVE_TRAILER`,
  async (route: { id: string }, { dispatch }) => {
    await axios.post(`/api/routes/remove-trailer`, { id: route.id });
    dispatch(getRouteStops(route.id));
  }
);

export const archiveRoute = createErrorReportingAsyncThunk(
  `${prefix}/ROUTE_ARCHIVE`,
  async ({ id }: { id: string }, { dispatch }) => {
    await axios.post(`/api/routes/archive`, { id });
    dispatch(
      notifyL({
        namespace: "notifications",
        key: "routeArchived",
        type: "success",
      })
    );
  }
);

export const addRoute = createErrorReportingAsyncThunk(
  `${prefix}/add-route`,
  async (data: { truckId?: string; intendedStartDate: string | null }) => {
    return axios
      .post("/api/routes", data || {})
      .then(getData)
      .then(tPromise.decode(routeT));
  }
);

const archiveRouteAndCreateNew = createErrorReportingAsyncThunk(
  `${prefix}/add-route-with-trailer`,
  async (data: { routeId: string }, { dispatch }) => {
    const route = await axios
      .post(`/api/routes/${data.routeId}/archive-and-create-new`, data)
      .then(getData)
      .then(tPromise.decode(routeT));
    dispatch(
      notifyL({
        namespace: "notifications",
        key: "routeArchived",
        type: "success",
      })
    );
    return { route, archivedRouteId: data.routeId };
  }
);

export const addRouteWithTrailerAndWaypoint = unwrap(archiveRouteAndCreateNew);

export const updateRouteNote = createErrorReportingAsyncThunk(
  `${prefix}/ROUTE_UPDATE_NOTE`,
  async (route: { id: string; note?: string }) => {
    await axios.put(`/api/routes/update-note`, {
      id: route.id,
      note: route.note,
    });
  }
);

export const SORT_PROPERTY_KEY = "undispatchedRoutesSortProperty";
export const SORT_ORDER_KEY = "undispatchedRoutesSortOrder";
const ROUTES_SORTING_KEY = "routesSorting";

// Seed the new multi-sort array from a stored value, or (non-destructively,
// so toggling the "multi-property-route-sort" feature back off still works)
// from the old single-sort keys. Old keys are left in place either way.
const migrateRoutesSorting = (): RouteSortSelection[] => {
  const stored = localStorage.getItem(ROUTES_SORTING_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as RouteSortSelection[];
    } catch {
      localStorage.removeItem(ROUTES_SORTING_KEY);
      return [];
    }
  }
  const oldProperty = localStorage.getItem(
    SORT_PROPERTY_KEY
  ) as RouteSortProperty | null;
  const oldOrder = localStorage.getItem(SORT_ORDER_KEY) as SortOrder | null;
  if (oldProperty) {
    return [{ property: oldProperty, order: oldOrder || "DESC" }];
  }
  return [];
};

const sortPropertyLocalStorage = localStorage.getItem(SORT_PROPERTY_KEY) as any;
const sortOrderLocalStorage = localStorage.getItem(SORT_ORDER_KEY) as any;

const initialState: types.RoutesState = {
  routeEntities: {},
  routeUpdates: {},
  routeIds: [],
  offeredCargoIds: [],
  sortProperty: sortPropertyLocalStorage || undefined,
  routesSortOrder: sortOrderLocalStorage || "DESC",
  routesSorting: migrateRoutesSorting(),
  isSearchForArchived: false,
  archivedSearchRequestId: null,
};

const sortRoutes = createErrorReportingAsyncThunk(
  `${prefix}/sort-routes`,
  async (
    arg: {
      sortProperty?: types.RouteSortProperty;
      sortOrder?: SortOrder;
    },
    thunkAPI
  ) => {
    const state: RootState = thunkAPI.getState();
    const routeIds = state.routes.routeIds;
    const trailers = state.data.trailers.entities;
    const sortProperty = arg.sortProperty || state.routes.sortProperty;
    const sortOrder: SortOrder =
      typeof arg.sortOrder !== "undefined"
        ? arg.sortOrder
        : state.routes.routesSortOrder;
    if (!sortProperty) {
      return { sortProperty, sortOrder, routeIds };
    }
    const routes = routeIds.map((routeId) => ({
      id: routeId,
      viewModel: state.data.routeViewModels.entities[routeId],
    }));
    const emptyRouteDate = sortOrder === "ASC" ? "0000-01-01" : "9999-12-31";
    const preSorted = sortBy(routes, "id");
    // TODO: this whole thing isn't even used, consider removing it
    const sorted = (() => {
      switch (sortProperty) {
        case "PICKUPDATE":
          return sortBy(
            preSorted,
            ({ viewModel }) => viewModel?.startDate || emptyRouteDate
          );
        case "DROPOFFDATE":
          return sortBy(
            preSorted,
            ({ viewModel }) => viewModel?.endDate || emptyRouteDate
          );
        case "TRUCKNUMBER":
          return sortBy(
            preSorted,
            ({ viewModel }) =>
              viewModel?.truckId && trailers[viewModel?.truckId]?.number
          );
        default:
          return routes;
      }
    })().map((x) => x.id);
    if (sortOrder === "DESC") {
      sorted.reverse();
    }
    thunkAPI.dispatch(
      routesSlice.actions.setRoutes({
        sortProperty,
        sortOrder,
        routeIds: sorted,
      })
    );
  }
);

export const setRoutesSortProperty = (sortProperty: types.RouteSortProperty) =>
  sortRoutes({ sortProperty });

export const setRoutesSortOrder = (o: SortOrder | boolean) => {
  const sortOrder = typeof o === "boolean" ? (o ? "ASC" : "DESC") : o;
  return sortRoutes({ sortOrder });
};

const routesSlice = createSlice({
  name: "routes",
  initialState,
  reducers: {
    setRoutesSortProperty: (
      state,
      action: PayloadAction<types.RouteSortProperty>
    ) => {
      state.sortProperty = action.payload;
    },
    setRoutesSortOrder: (state, action: PayloadAction<SortOrder>) => {
      state.routesSortOrder = action.payload;
    },
    setRoutes: (
      state,
      action: PayloadAction<{
        sortProperty?: types.RouteSortProperty;
        sortOrder: SortOrder;
        routeIds: string[];
      }>
    ) => {
      state.sortProperty = action.payload.sortProperty;
      state.routesSortOrder = action.payload.sortOrder;
      state.routeIds = action.payload.routeIds;
    },
    addRouteSort: (state, action: PayloadAction<RouteSortProperty>) => {
      state.routesSorting.push({ property: action.payload, order: "ASC" });
      localStorage.setItem(
        ROUTES_SORTING_KEY,
        JSON.stringify(state.routesSorting)
      );
    },
    removeRouteSort: (state, action: PayloadAction<RouteSortProperty>) => {
      state.routesSorting = state.routesSorting.filter(
        (x) => x.property !== action.payload
      );
      localStorage.setItem(
        ROUTES_SORTING_KEY,
        JSON.stringify(state.routesSorting)
      );
    },
    switchRouteSortOrder: (state, action: PayloadAction<RouteSortProperty>) => {
      const index = state.routesSorting.findIndex(
        (x) => x.property === action.payload
      );
      if (index === -1) return;
      state.routesSorting[index].order =
        state.routesSorting[index].order === "ASC" ? "DESC" : "ASC";
      localStorage.setItem(
        ROUTES_SORTING_KEY,
        JSON.stringify(state.routesSorting)
      );
    },
    setIsSearchingForArchived: (state, action: PayloadAction<boolean>) => {
      state.isSearchForArchived = action.payload;
      if (!action.payload) {
        state.archivedSearchRequestId = null;
        delete state.archivedSearchRouteIds;
        delete state.relevantCargoFilter;
      }
    },
  },
  extraReducers: (builder) => {
    // Get
    builder.addCase(routeUpdated, (state, action) => {
      const route = action.payload;
      if (!state.routeIds.includes(route.id)) {
        state.routeIds.push(route.id);
      }
      state.routeEntities[route.id] = route;
    });
    builder.addCase(addRoute.fulfilled, (state, action) => {
      const route = action.payload;
      if (!state.routeIds.includes(route.id)) {
        state.routeIds.unshift(route.id);
      }
      state.routeEntities[route.id] = route;
    });
    // Update
    builder.addCase(updateRouteRadius.fulfilled, (state, action) => {
      const { routeId, radius } = action.meta.arg;
      const route = state.routeEntities[routeId];
      if (route) {
        route.radius = radius;
      }
    });
    builder.addCase(updateRouteTruck.fulfilled, (state, action) => {
      const { id, truckId } = action.meta.arg;
      const route = state.routeEntities[id];
      if (route) {
        route.truckId = truckId;
      }
    });
    builder.addCase(removeRouteTruck.fulfilled, (state, action) => {
      const { id } = action.meta.arg;
      const route = state.routeEntities[id];
      if (route) {
        route.truckId = null;
      }
    });
    builder.addCase(updateRouteNote.fulfilled, (state, action) => {
      const { id, note } = action.meta.arg;
      const route = state.routeEntities[id];
      if (route) {
        route.note = note;
      }
    });
    builder.addCase(updateRouteName.fulfilled, (state, action) => {
      const { id, route_name } = action.meta.arg;
      const route = state.routeEntities[id];
      if (route) {
        route.route_name = route_name;
      }
    });
    builder.addCase(cargoActions.updateSmartMatch.pending, (state, action) => {
      const { cargoId, smartMatch } = action.meta.arg;
      if (smartMatch) {
        state.offeredCargoIds.push(cargoId);
      } else {
        state.offeredCargoIds = state.offeredCargoIds.filter(
          (x) => x !== cargoId
        );
      }
    });
    builder.addCase(cargoDeleted, (state, action) => {
      const { cargoId } = action.payload;
      {
        const idx = state.offeredCargoIds.indexOf(cargoId);
        if (idx !== -1) state.offeredCargoIds.splice(idx, 1);
      }
    });
    builder.addCase(getAllArchivedRoutes.pending, (state, action) => {
      state.archivedSearchRequestId = action.meta.requestId;
    });
    builder.addCase(getAllArchivedRoutes.rejected, (state, action) => {
      if (state.archivedSearchRequestId === action.meta.requestId) {
        state.archivedSearchRequestId = null;
      }
    });
    builder.addCase(getAllArchivedRoutes.fulfilled, (state, action) => {
      state.archivedSearchRequestId = null;
      // Overwrite unarchived routes, with archived routes
      const { routes } = action.payload;
      const routeIds = routes.map((x) => x.id);
      state.routeIds = routeIds;
      state.offeredCargoIds = [];
      state.routeEntities = keyBy(routes, "id");
      state.archivedSearchRouteIds = routeIds;
      // When "Only show relevant cargos" is checked, remember the criteria the
      // user searched by so the cargo cards can be filtered down to matches.
      const arg = action.meta.arg;
      state.relevantCargoFilter = arg.onlyShowRelevantCargos
        ? {
            clientId: arg.clientId ?? null,
            descriptionsSearch: arg.descriptionsSearch || null,
            cargoTagId: arg.cargoTagId || null,
            cargoRef: arg.cargoRef || null,
          }
        : null;
    });
    builder.addCase(archiveRouteAndCreateNew.fulfilled, (state, action) => {
      const { archivedRouteId, route } = action.payload;
      state.routeIds = [
        ...state.routeIds.filter((id) => id !== archivedRouteId),
        route.id,
      ];
      state.routeEntities[route.id] = route;
      delete state.routeEntities[archivedRouteId];
    });
    builder
      .addCase(unarchiveRoute.fulfilled, (state, action) => {
        // Remove unarchived route from archived route ids
        const { id } = action.meta.arg;
        state.archivedSearchRouteIds = state.archivedSearchRouteIds?.filter(
          (x) => x !== id
        );
      })
      .addCase(addDriver.pending, (state, action) => {
        const { routeId, driverId } = action.meta.arg;
        const route = state.routeEntities[routeId];
        state.routeUpdates[routeId] = route;
        state.routeEntities[routeId] = route && {
          ...route,
          drivers: [...route.drivers, driverId],
        };
      })
      .addCase(addDriver.fulfilled, (state, action) => {
        const { routeId } = action.meta.arg;
        delete state.routeUpdates[routeId];
      })
      .addCase(addDriver.rejected, (state, action) => {
        const { routeId } = action.meta.arg;
        const route = state.routeUpdates[routeId];
        if (route) {
          state.routeEntities[routeId] = route;
          delete state.routeUpdates[routeId];
        }
      })
      .addCase(removeDriver.pending, (state, action) => {
        const { routeId, driverId } = action.meta.arg;
        const route = state.routeEntities[routeId];
        state.routeUpdates[routeId] = route;
        state.routeEntities[routeId] = route && {
          ...route,
          drivers: route.drivers.filter((x) => x !== driverId),
        };
      })
      .addCase(assignToTeam.fulfilled, (state, action) => {
        const { routeId } = action.meta.arg;
        state.routeEntities[routeId] = action.payload;
      })
      .addCase(removeDriver.fulfilled, (state, action) => {
        const { routeId } = action.meta.arg;
        delete state.routeUpdates[routeId];
      })
      .addCase(removeDriver.rejected, (state, action) => {
        const { routeId } = action.meta.arg;
        const route = state.routeUpdates[routeId];
        if (route) {
          state.routeEntities[routeId] = route;
          delete state.routeUpdates[routeId];
        }
      })
      .addCase(dispatchingActions.clear, (state) => {
        delete state.sortProperty;
        state.routesSorting = [];
        localStorage.setItem(ROUTES_SORTING_KEY, JSON.stringify([]));
      });
  },
});

export const {
  setIsSearchingForArchived,
  addRouteSort,
  removeRouteSort,
  switchRouteSortOrder,
} = routesSlice.actions;
export default routesSlice.reducer;
